import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { formatDate } from "@/lib/dateFmt";
import { STAGE_LABEL } from "@/lib/statusMap";
import { FORM_TYPE_LABEL } from "@/components/FormDetails";

/**
 * Phase 15 — Print-friendly dashboard view. Chrome hidden via @media print;
 * only header + table appear on paper.
 */
export default function DashboardPrintView() {
  const [params] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const filters = {
      form_type: params.get("form_type") || undefined,
      department: params.get("department") || undefined,
      current_stage: params.get("current_stage") || undefined,
      status: params.get("status") || undefined,
    };
    api.get("/requisitions", { params: { filter: params.get("tab") || "all" } })
      .then((r) => {
        const filtered = (r.data || []).filter((row) => {
          if (filters.form_type && row.form_type !== filters.form_type) return false;
          if (filters.department && (row.form_data?.mainDept || "") !== filters.department) return false;
          if (filters.current_stage && row.current_stage !== filters.current_stage) return false;
          if (filters.status && row.status !== filters.status) return false;
          return true;
        });
        setRows(filtered);
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [params]);

  return (
    <div className="p-6 bg-white text-[#1f2320]" data-testid="dashboard-print">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { size: A4 landscape; margin: 10mm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
      <div className="flex items-center justify-between border-b border-[#7a1f2b] pb-3 mb-4">
        <div>
          <h1 className="font-serif text-2xl font-bold text-[#7a1f2b]">VGWS Requisitions</h1>
          <div className="text-xs text-[#6e7770]">
            Print snapshot · {formatDate(new Date().toISOString(), { includeTime: true })}
          </div>
        </div>
        <button onClick={() => window.print()} className="btn-vgws no-print" data-testid="print-btn">
          Print
        </button>
      </div>
      {loading ? <div>Loading…</div> : (
        <table className="w-full text-xs border-collapse" data-testid="print-table">
          <thead>
            <tr className="bg-[#7a1f2b] text-white">
              <th className="px-2 py-1 text-left">Ref</th>
              <th className="px-2 py-1 text-left">Form Type</th>
              <th className="px-2 py-1 text-left">Applicant</th>
              <th className="px-2 py-1 text-left">Main Dept</th>
              <th className="px-2 py-1 text-left">Status</th>
              <th className="px-2 py-1 text-left">Current Stage</th>
              <th className="px-2 py-1 text-left">Submitted</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.ref_code} className="odd:bg-white even:bg-[#faf5eb]">
                <td className="px-2 py-1 border-b border-[#e2d7c3]">{r.ref_code}</td>
                <td className="px-2 py-1 border-b border-[#e2d7c3]">{FORM_TYPE_LABEL[r.form_type] || r.form_type}</td>
                <td className="px-2 py-1 border-b border-[#e2d7c3]">{r.applicant_name || ""}</td>
                <td className="px-2 py-1 border-b border-[#e2d7c3]">{r.form_data?.mainDept || ""}</td>
                <td className="px-2 py-1 border-b border-[#e2d7c3]">{r.status}</td>
                <td className="px-2 py-1 border-b border-[#e2d7c3]">{STAGE_LABEL[r.current_stage] || r.current_stage}</td>
                <td className="px-2 py-1 border-b border-[#e2d7c3]">{formatDate(r.submitted_at || r.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
